import {
  skipToken,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import {
  createCategory,
  deleteCategory,
  updateCategory,
  fetchCategoriesWithFilter,
  fetchTemplates,
  createTemplate,
  fetchTemplateById,
  deleteTemplate,
  updateTemplate,
  fetchTemplateDeploy,
  updateTemplateDeploy,
  deployTemplateSave,
  removeRecentFile,
  uploadRecentFile,
  downloadFile,
} from './crud';
import {
  CreateTemplateResourceDto,
  TemplateCategory,
  TemplateDeploy,
  UpdateTemplateResourceDto,
  TemplateResource,
} from '@morph-mapper/types';
import { reviveState } from '@morph-mapper/utils';
import { useNotify } from '../../hooks/use-notify';
import { AxiosError } from 'axios';
import { getAxiosErrorMessage } from '../errors';
import { t } from 'i18next';
import { CategoryFilter, TemplateFilter } from '../types';

export const useCreateTemplate = () => {
  const queryClient = useQueryClient();
  const { success, error } = useNotify();

  return useMutation({
    mutationFn: (template: CreateTemplateResourceDto) =>
      createTemplate(template),
    onSuccess: () => {
      success(t('message.success.templateCreated'));
      queryClient.invalidateQueries({ queryKey: ['templates'] });
    },
    onError: (e) => {
      error(
        getAxiosErrorMessage(e, t('message.error.templateCreationFailed')),
      );
    },
  });
};

export const useTemplatesWithFilter = ({ operatorId }: TemplateFilter) => {
  return useQuery({
    queryKey: ['templates', operatorId],
    queryFn: () => fetchTemplates({ operatorId }),
  });
};

export const useTemplate = (id: string) => {
  return useQuery({
    queryKey: ['template', id],
    queryFn: id ? () => fetchTemplateById(id) : skipToken,
    select: (data) => ({
      ...data,
      save: data.save ? reviveState(data.save) : undefined,
    }),
  });
};

export const useDeleteTemplate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => deleteTemplate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['templates'] });
    },
  });
};
export const useRemoveRecentFile = () => {
  const { error: notifyError } = useNotify();

  return useMutation({
    mutationFn: async ({
      templateId,
      fileId,
    }: {
      templateId: string;
      fileId: string;
    }): Promise<TemplateResource> => {
      return await removeRecentFile(templateId, fileId);
    },
    onError: (e) => {
      notifyError(getAxiosErrorMessage(e, 'Failed to remove recent file'));
    },
  });
};

export const useUploadRecentFile = () => {
  const { error: notifyError } = useNotify();

  return useMutation({
    mutationFn: async ({
      templateId,
      file,
      userId,
    }: {
      templateId: string;
      file: File;
      userId: string;
    }): Promise<TemplateResource> => {
      return await uploadRecentFile(templateId, file, userId);
    },
    onError: (e) => {
      notifyError(getAxiosErrorMessage(e, 'Failed to upload recent file'));
    },
  });
};

export const useDownloadRecentFile = () => {
  const { error: notifyError } = useNotify();

  return useMutation({
    mutationFn: async (fileId: string): Promise<string> => {
      return await downloadFile(fileId);
    },
    onError: (e) => {
      notifyError(getAxiosErrorMessage(e, 'Failed to download recent file'));
    },
  });
};

export const useUpdateTemplate = () => {
  const queryClient = useQueryClient();
  const { success, error } = useNotify();

  return useMutation({
    mutationFn: ({
      id,
      template,
    }: {
      id: string;
      template: UpdateTemplateResourceDto;
    }) => updateTemplate(id, template),
    onSuccess: () => {
      success(t('message.success.templateSaved'));
      queryClient.invalidateQueries({ queryKey: ['templates'] });
      queryClient.invalidateQueries({ queryKey: ['template'] });
    },
    onError: (e) => {
      error(getAxiosErrorMessage(e, t('message.error.templateSavingFailed')));
    },
  });
};

export const useTemplateDeploy = (
  id: string,
  options?: { enabled?: boolean },
) => {
  const isActive = options?.enabled;

  return useQuery({
    queryKey: ['deploy', id],
    queryFn: isActive ? () => fetchTemplateDeploy(id) : skipToken,
    enabled: isActive,
  });
};

export const useUpdateTemplateDeploy = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, content }: { id: string; content: any }) =>
      updateTemplateDeploy(id, content),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['template-refs'] });
    },
  });
};

// Updates or creates a deployed template (if deployPath does not exist on template)
export const useUpdateDeployTemplateSave = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      save,
    }: {
      id: string;
      save: Omit<TemplateDeploy, 'id'>;
    }) => deployTemplateSave(id, save),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['template-refs'] });
    },
  });
};

/**
 * Queries: Category
 **/

// Fetches all categories by operator id and current domain
export const useCategoriesWithFilter = ({
  operatorId,
  domain,
}: CategoryFilter) => {
  const isActive = operatorId !== undefined && domain !== undefined;

  return useQuery({
    queryKey: ['categories', operatorId, domain],
    queryFn: isActive
      ? () => fetchCategoriesWithFilter({ operatorId, domain })
      : skipToken,
    initialData: [],
  });
};

// Creates a new category
export const useCreateCategory = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (category: Omit<TemplateCategory, 'id'>) =>
      createCategory(category),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['categories'] });
    },
  });
};

// Deletes a category
export const useDeleteCategory = () => {
  const queryClient = useQueryClient();
  const { warn, error, success } = useNotify();

  return useMutation({
    mutationFn: (id: string) => deleteCategory(id),
    onSuccess: () => {
      success(t('message.success.categoryDeleted'));
      queryClient.invalidateQueries({ queryKey: ['categories'] });
    },
    onError: (e) => {
      if (e instanceof AxiosError && e.response?.data?.message) {
        warn(e.response.data.message);
        return;
      }

      error(getAxiosErrorMessage(e, 'An error occurred'));
    },
  });
};
// Updates a category
export const useUpdateCategory = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      category,
    }: {
      id: string;
      category: Partial<TemplateCategory>;
    }) => updateCategory(id, category),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['categories'] });
    },
  });
};
